import { useText } from "../settings/context";
import { contrastReport, gradientRefusal } from "../theme/palette";
import { DEFAULT_BRAND, DEFAULT_PAGE, usePageGradient, usePageImage } from "../theme/useTheme";

/**
 * THE PROMISE, MEASURED.
 *
 * Two settings produce a whole palette, and the claim attached to that is that no
 * pair of them can make the site unreadable. A claim nobody can check is worth
 * nothing, so it is checked here, live, in the ratio the standard actually names.
 *
 * The reading is the WORST of the three that matter: body text on its ground,
 * text laid on the brand, and the brand used as text. Showing the best of them
 * would flatter the palette and hide the one place it fell short.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function ContrastMeter() {
  const brand = useText("FO_MEETING_V2_BRAND_COLOR", DEFAULT_BRAND);
  const page = useText("FO_MEETING_V2_PAGE_COLOR", DEFAULT_PAGE);
  const gradient = usePageGradient();
  /* L'image aussi : le texte se mesure contre ce que son voile laisse passer. */
  const image = usePageImage();
  /* Un dégradé demandé qui ne s'applique pas : le dire ici, là où l'on règle les deux
     couleurs, plutôt que de laisser l'organisateur chercher pourquoi le fond reste uni. */
  const refusal = gradientRefusal(page, gradient);

  let worst: number;
  try {
    const report = contrastReport(brand, page, gradient, image);
    worst = Math.min(report.text, report.onBrand, report.brandText);
  } catch {
    // Half-typed hex: say nothing rather than a wrong number.
    return null;
  }

  const ok = worst >= 4.5;
  return (
    <>
      <div className={`meter ${ok ? "meter--ok" : "meter--warn"}`}>
        <i aria-hidden="true" />
        <span>
          Contraste le plus faible&nbsp;: <b>{worst.toFixed(1)}:1</b> ·{" "}
          {worst >= 7 ? "excellent" : ok ? "lisible" : "insuffisant"}
        </span>
      </div>
      {refusal ? (
        <div className="meter meter--warn" role="status">
          <i aria-hidden="true" />
          <span>
            {refusal === "opposite"
              ? "Le fond reste uni : une couleur claire et une couleur sombre ne peuvent pas porter le même texte. Choisissez deux couleurs claires, ou deux sombres."
              : "Le fond reste uni tant que la seconde couleur du dégradé n'est pas choisie."}
          </span>
        </div>
      ) : null}
    </>
  );
}
